import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
    Bell,
    Check,
    CheckCircle2,
    Copy,
    ExternalLink,
    RefreshCw,
    Sparkles,
    TrendingDown,
    TrendingUp,
    X,
    AlertTriangle,
    Clock,
    ShieldCheck,
} from 'lucide-react';
import {
    fetchGeoOptimizerDashboard,
    applyOptimizerProposal,
    rejectOptimizerProposal,
    OptimizerProposal,
} from '../../services/geoOptimizerApi';

export interface Rolling72hSnapshot {
    evaluated_at?: string;
    window?: { from: string; to: string; hours: number };
    campaign_id?: string;
    campaign_name?: string;
    zone?: 'green' | 'yellow' | 'red';
    zone_label?: string;
    current_daytime_budget_dkk?: number;
    recommended_daytime_budget_dkk?: number;
    spend_72h_dkk?: number;
    rev_72h_dkk?: number;
    purchases_72h?: number;
    instant_roas_72h?: number;
    projected_ltv_roas_72h?: number;
    salus_approved_72h?: number;
    cpa_salus_72h_dkk?: number;
    spend_today_dkk?: number;
    rev_today_dkk?: number;
    purchases_today?: number;
    instant_roas_today?: number;
    salus_approved_today?: number;
    cpa_salus_today_dkk?: number;
    today_salus_total?: number;
    today_salus_pending?: number;
    ads_breakdown?: Array<{
        ad_id: string;
        ad_name: string;
        spend_72h_dkk: number;
        impressions_72h: number;
        frequency_72h: number;
        link_clicks_72h: number;
        link_ctr_pct: number;
        cpc_dkk: number;
        salus_approved_72h: number;
        cpa_salus_dkk: number;
        purchases_72h: number;
        revenue_72h_dkk: number;
        instant_roas_72h: number;
        needs_attention: boolean;
        fatigue_reasons: string[];
    }>;
}

const fmtDkk = (n?: number | null) =>
    `${Math.round(Number(n || 0)).toLocaleString('da-DK')} DKK`;

const fmtPct = (ratio?: number | null) =>
    `${(Number(ratio || 0) * 100).toFixed(1)}%`;

const SNOOZE_STORAGE_KEY = 'afima_se_72h_snoozed_v1';

export interface SnoozeEntry {
    until: number; // epoch ms
    label: string;
    snoozedAt: number;
}

export function loadSnoozeMap(): Record<string, SnoozeEntry> {
    try {
        const raw = localStorage.getItem(SNOOZE_STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw) as Record<string, SnoozeEntry>;
        const now = Date.now();
        const active: Record<string, SnoozeEntry> = {};
        for (const [k, v] of Object.entries(parsed)) {
            if (v && typeof v.until === 'number' && v.until > now) {
                active[k] = v;
            }
        }
        return active;
    } catch {
        return {};
    }
}

export function saveSnoozeEntry(id: number, hours: number, label: string): Record<string, SnoozeEntry> {
    const current = loadSnoozeMap();
    const now = Date.now();
    current[String(id)] = {
        until: now + hours * 3600 * 1000,
        label,
        snoozedAt: now,
    };
    try {
        localStorage.setItem(SNOOZE_STORAGE_KEY, JSON.stringify(current));
        window.dispatchEvent(new CustomEvent('afima-72h-snooze-changed'));
    } catch {
        // ignore storage errors
    }
    return current;
}

export function clearSnoozeEntry(id: number): Record<string, SnoozeEntry> {
    const current = loadSnoozeMap();
    delete current[String(id)];
    try {
        localStorage.setItem(SNOOZE_STORAGE_KEY, JSON.stringify(current));
        window.dispatchEvent(new CustomEvent('afima-72h-snooze-changed'));
    } catch {
        // ignore storage errors
    }
    return current;
}

export const NotificationCenter: React.FC = () => {
    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState<'pending' | 'history'>('pending');
    const [copiedId, setCopiedId] = useState<number | null>(null);
    const [snoozeMenuId, setSnoozeMenuId] = useState<number | null>(null);
    const [snoozeMap, setSnoozeMap] = useState<Record<string, SnoozeEntry>>(() => loadSnoozeMap());
    const [toastMsg, setToastMsg] = useState<string | null>(null);
    const panelRef = useRef<HTMLDivElement | null>(null);
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    useEffect(() => {
        const syncSnooze = () => setSnoozeMap(loadSnoozeMap());
        window.addEventListener('afima-72h-snooze-changed', syncSnooze);
        const timer = setInterval(syncSnooze, 30_000);
        return () => {
            window.removeEventListener('afima-72h-snooze-changed', syncSnooze);
            clearInterval(timer);
        };
    }, []);

    const { data, isFetching, refetch } = useQuery({
        queryKey: ['se-rolling-72h-notifications'],
        queryFn: () => fetchGeoOptimizerDashboard('se', 3),
        refetchInterval: 60_000,
        staleTime: 30_000,
    });

    const latestRun = data?.latest_run;
    const snap = (((data?.portfolio as any)?.rolling_72h) ||
        ((latestRun as any)?.portfolio_snapshot?.rolling_72h)) as Rolling72hSnapshot | undefined;
    const allProposals: OptimizerProposal[] = data?.proposals || [];
    const pendingProposals = allProposals.filter(
        (p) => p.status === 'pending' && !snoozeMap[String(p.id)]
    );
    const historyProposals = allProposals.filter(
        (p) => p.status === 'applied' || p.status === 'rejected' || Boolean(snoozeMap[String(p.id)])
    );

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        if (open) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    const showToast = (msg: string) => {
        setToastMsg(msg);
        setTimeout(() => setToastMsg(null), 4500);
    };

    const approveMut = useMutation({
        mutationFn: (proposalId: number) => applyOptimizerProposal(proposalId),
        onSuccess: (_res, proposalId) => {
            const p = allProposals.find((x) => x.id === proposalId);
            const payload = (p?.action_payload || {}) as Record<string, any>;
            if (payload.action === 'update_live_budget') {
                showToast(`✓ Budget opdateret live på Meta til ${fmtDkk(payload.daily_budget)}/dag!`);
            } else if (payload.notification_kind === 'creative_sanitizing') {
                showToast(`✓ Markeret som gjort: ${payload.ad_name || p?.title}`);
            } else {
                showToast(`✓ Bekræftet og markeret som gennemført!`);
            }
            queryClient.invalidateQueries({ queryKey: ['se-rolling-72h-notifications'] });
        },
        onError: (err: any) => {
            showToast(`⚠️ Fejl ved godkendelse: ${err?.message || 'Ukendt fejl'}`);
        },
    });

    const rejectMut = useMutation({
        mutationFn: ({ id }: { id: number; reason?: string }) => rejectOptimizerProposal(id),
        onSuccess: () => {
            showToast('Forslag afvist — nuværende indstilling fastholdes.');
            queryClient.invalidateQueries({ queryKey: ['se-rolling-72h-notifications'] });
        },
    });

    const handleSnooze = (p: OptimizerProposal, hours: number, label: string, persistBackend = false) => {
        const nextMap = saveSnoozeEntry(p.id, hours, label);
        setSnoozeMap(nextMap);
        setSnoozeMenuId(null);
        showToast(`⏰ Udskudt (${label}): "${p.title}"`);
        if (persistBackend) {
            rejectOptimizerProposal(p.id)
                .then(() => {
                    queryClient.invalidateQueries({ queryKey: ['se-rolling-72h-notifications'] });
                })
                .catch(() => {});
        }
    };

    const handleUnsnooze = (id: number) => {
        const nextMap = clearSnoozeEntry(id);
        setSnoozeMap(nextMap);
        showToast('✓ Punkt taget op igen under "Afventer dig"');
    };

    const handleCopyPrompt = (p: OptimizerProposal) => {
        const payload = (p.action_payload || {}) as Record<string, any>;
        const text =
            payload.antigravity_prompt ||
            `Analyser venligst "${p.target_object_name}": ${p.rationale}`;
        navigator.clipboard.writeText(text);
        setCopiedId(p.id);
        showToast('📋 Analyse-prompt kopieret! Indsæt den i Antigravity-chatten.');
        setTimeout(() => setCopiedId(null), 3000);
    };

    const zone = snap?.zone || 'yellow';
    const zoneStyles =
        zone === 'green'
            ? {
                  badgeBg: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
                  headerBg: 'from-emerald-950/80 to-slate-900',
                  roasColor: 'text-emerald-400',
              }
            : zone === 'red'
            ? {
                  badgeBg: 'bg-rose-500/20 text-rose-300 border-rose-500/40',
                  headerBg: 'from-rose-950/80 to-slate-900',
                  roasColor: 'text-rose-400',
              }
            : {
                  badgeBg: 'bg-amber-500/20 text-amber-300 border-amber-500/40',
                  headerBg: 'from-amber-950/60 to-slate-900',
                  roasColor: 'text-amber-400',
              };

    return (
        <div className="relative" ref={panelRef}>
            {/* Bell Trigger Button */}
            <button
                onClick={() => setOpen((v) => !v)}
                className={`relative flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                    pendingProposals.length > 0
                        ? 'bg-amber-500/10 border-amber-500/40 text-amber-200 hover:bg-amber-500/20'
                        : 'bg-slate-800/70 border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white'
                }`}
                title="72-Timers Rullende Styring & Notifikationer"
            >
                <div className="relative">
                    <Bell className="w-4 h-4" />
                    {pendingProposals.length > 0 && (
                        <span className="absolute -top-1.5 -right-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-orange-500 px-1 text-[10px] font-bold text-white shadow">
                            {pendingProposals.length}
                        </span>
                    )}
                </div>
                <span className="hidden sm:inline">
                    {pendingProposals.length > 0
                        ? `${pendingProposals.length} afventer godkendelse`
                        : '72t Styring'}
                </span>
            </button>

            {/* Dropdown / Slide-over Drawer */}
            {open && (
                <div className="fixed sm:absolute right-2 sm:right-0 top-14 sm:top-11 w-[calc(100vw-1rem)] sm:w-[500px] max-h-[84vh] flex flex-col rounded-2xl bg-slate-900 border border-slate-700/90 shadow-2xl z-50 overflow-hidden">
                    {/* Top Header */}
                    <div className={`p-4 bg-gradient-to-r ${zoneStyles.headerBg} border-b border-slate-800`}>
                        <div className="flex items-start justify-between gap-2">
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-400">
                                        Sverige · Rullende 72-Timers Styring
                                    </span>
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800/80 text-slate-300 border border-slate-700">
                                        Dagligt kl. 17:00
                                    </span>
                                </div>
                                <h3 className="text-sm font-bold text-white mt-1">
                                    {snap?.zone_label || 'SL-SE Kvalificerad tratt v2 (Köp)'}
                                </h3>
                            </div>
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => refetch()}
                                    disabled={isFetching}
                                    className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800/70 transition-colors"
                                    title="Opdater 72t status"
                                >
                                    <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin text-orange-400' : ''}`} />
                                </button>
                                <button
                                    onClick={() => setOpen(false)}
                                    className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800/70 transition-colors"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        {/* 72h Executive Mini-KPIs */}
                        {snap && (
                            <div className="grid grid-cols-3 gap-2 mt-3">
                                <div className="rounded-xl bg-slate-950/60 border border-slate-800/90 p-2.5">
                                    <div className="text-[10px] text-slate-400">72t Instant ROAS</div>
                                    <div className={`text-base font-bold ${zoneStyles.roasColor}`}>
                                        {fmtPct(snap.instant_roas_72h)}
                                    </div>
                                    <div className="text-[10px] text-slate-400">
                                        ~{fmtPct(snap.projected_ltv_roas_72h)} inkl. LTV
                                    </div>
                                </div>
                                <div className="rounded-xl bg-slate-950/60 border border-slate-800/90 p-2.5">
                                    <div className="text-[10px] text-slate-400">72t Spend → Salg</div>
                                    <div className="text-xs font-bold text-white mt-0.5">
                                        {Math.round(snap.spend_72h_dkk || 0).toLocaleString('da-DK')} →{' '}
                                        {Math.round(snap.rev_72h_dkk || 0).toLocaleString('da-DK')} kr.
                                    </div>
                                    <div className="text-[10px] text-slate-400">
                                        {snap.purchases_72h || 0} køb ({snap.purchases_today || 0} i dag)
                                    </div>
                                </div>
                                <div className="rounded-xl bg-slate-950/60 border border-slate-800/90 p-2.5">
                                    <div className="text-[10px] text-slate-400">Approved by Salus</div>
                                    <div className="text-xs font-bold text-white mt-0.5">
                                        {snap.salus_approved_72h || 0} stk ({Math.round(snap.cpa_salus_72h_dkk || 0)} kr.)
                                    </div>
                                    <div className="text-[10px] text-amber-300/90">
                                        +{snap.today_salus_pending || 0} afventer bank i dag
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Toast Feedback Banner */}
                    {toastMsg && (
                        <div className="px-4 py-2 bg-emerald-950/90 border-b border-emerald-700/50 text-emerald-200 text-xs font-medium flex items-center justify-between">
                            <span>{toastMsg}</span>
                            <button onClick={() => setToastMsg(null)} className="text-emerald-400 hover:text-white">
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    )}

                    {/* Tabs */}
                    <div className="flex border-b border-slate-800 bg-slate-950/40 px-4 pt-2 gap-4 text-xs">
                        <button
                            onClick={() => setTab('pending')}
                            className={`pb-2 font-semibold border-b-2 transition-colors flex items-center gap-1.5 ${
                                tab === 'pending'
                                    ? 'border-orange-500 text-white'
                                    : 'border-transparent text-slate-400 hover:text-slate-200'
                            }`}
                        >
                            <span>Afventer dig</span>
                            <span
                                className={`px-1.5 py-0.2 rounded-full text-[10px] ${
                                    pendingProposals.length > 0
                                        ? 'bg-orange-500/20 text-orange-300 border border-orange-500/30'
                                        : 'bg-slate-800 text-slate-400'
                                }`}
                            >
                                {pendingProposals.length}
                            </span>
                        </button>
                        <button
                            onClick={() => setTab('history')}
                            className={`pb-2 font-semibold border-b-2 transition-colors flex items-center gap-1.5 ${
                                tab === 'history'
                                    ? 'border-orange-500 text-white'
                                    : 'border-transparent text-slate-400 hover:text-slate-200'
                            }`}
                        >
                            <span>Historik / Gjort</span>
                            <span className="px-1.5 py-0.2 rounded-full text-[10px] bg-slate-800 text-slate-400">
                                {historyProposals.length}
                            </span>
                        </button>
                    </div>

                    {/* Notification List */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-3">
                        {tab === 'pending' && pendingProposals.length === 0 && (
                            <div className="text-center py-8 px-4 rounded-xl bg-slate-950/40 border border-slate-800/80">
                                <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2 opacity-90" />
                                <p className="text-sm font-semibold text-white">Alt er gennemgået og godkendt!</p>
                                <p className="text-xs text-slate-400 mt-1">
                                    Næste automatiske 72-timers evaluering kører kl. 17:00 CET (og sender dig en e-mail + notifikation her).
                                </p>
                            </div>
                        )}

                        {tab === 'pending' &&
                            pendingProposals.map((p) => {
                                const payload = (p.action_payload || {}) as Record<string, any>;
                                const isBudget = p.proposal_type === 'budget_shift';
                                const isCreative =
                                    p.proposal_type === 'ad_pause' || payload.notification_kind === 'creative_sanitizing';
                                const isLiveBudgetChange = payload.action === 'update_live_budget';
                                const pZone = payload.zone || zone;

                                return (
                                    <div
                                        key={p.id}
                                        className="rounded-xl bg-slate-950/80 border border-slate-800 p-3.5 space-y-2.5 hover:border-slate-700 transition-colors"
                                    >
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="flex items-center gap-1.5">
                                                {isBudget ? (
                                                    pZone === 'green' ? (
                                                        <TrendingUp className="w-4 h-4 text-emerald-400 shrink-0" />
                                                    ) : pZone === 'red' ? (
                                                        <TrendingDown className="w-4 h-4 text-rose-400 shrink-0" />
                                                    ) : (
                                                        <ShieldCheck className="w-4 h-4 text-amber-400 shrink-0" />
                                                    )
                                                ) : (
                                                    <Sparkles className="w-4 h-4 text-sky-400 shrink-0" />
                                                )}
                                                <span className="text-xs font-bold text-white leading-snug">
                                                    {p.title}
                                                </span>
                                            </div>
                                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-800 text-slate-300 border border-slate-700 shrink-0">
                                                {isBudget ? '72t Budget' : 'Creative Hygiene'}
                                            </span>
                                        </div>

                                        <p className="text-xs text-slate-300 leading-relaxed">{p.rationale}</p>

                                        {/* Action Buttons */}
                                        <div className="flex flex-wrap items-center gap-2 pt-1">
                                            {isBudget && isLiveBudgetChange && (
                                                <>
                                                    <button
                                                        onClick={() => approveMut.mutate(p.id)}
                                                        disabled={approveMut.isPending}
                                                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white shadow transition-all ${
                                                            pZone === 'green'
                                                                ? 'bg-emerald-600 hover:bg-emerald-500'
                                                                : 'bg-rose-600 hover:bg-rose-500'
                                                        }`}
                                                    >
                                                        <Check className="w-3.5 h-3.5" />
                                                        <span>
                                                            {pZone === 'green'
                                                                ? `Godkend opjustering (${fmtDkk(payload.daily_budget)}/dag)`
                                                                : `Godkend nedjustering (${fmtDkk(payload.daily_budget)}/dag)`}
                                                        </span>
                                                    </button>
                                                    <button
                                                        onClick={() =>
                                                            rejectMut.mutate({
                                                                id: p.id,
                                                                reason: 'Afvist i Notification Center — fastholder nuværende budget',
                                                            })
                                                        }
                                                        disabled={rejectMut.isPending}
                                                        className="px-2.5 py-1.5 rounded-lg text-xs font-medium bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700"
                                                    >
                                                        Afvis (Behold {fmtDkk(payload.previous_daily_budget)})
                                                    </button>
                                                </>
                                            )}

                                            {isBudget && !isLiveBudgetChange && (
                                                <button
                                                    onClick={() => approveMut.mutate(p.id)}
                                                    disabled={approveMut.isPending}
                                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-600 hover:bg-amber-500 text-white shadow transition-all"
                                                >
                                                    <Check className="w-3.5 h-3.5" />
                                                    <span>Bekræft fastholdelse ({fmtDkk(payload.daily_budget)}/dag)</span>
                                                </button>
                                            )}

                                            {isCreative && (
                                                <>
                                                    <button
                                                        onClick={() => handleCopyPrompt(p)}
                                                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-sky-500/15 hover:bg-sky-500/25 text-sky-300 border border-sky-500/30 transition-colors"
                                                    >
                                                        <Copy className="w-3.5 h-3.5" />
                                                        <span>
                                                            {copiedId === p.id
                                                                ? 'Kopieret til udklipsholder!'
                                                                : 'Kopiér prompt til Antigravity'}
                                                        </span>
                                                    </button>
                                                    <button
                                                        onClick={() => approveMut.mutate(p.id)}
                                                        disabled={approveMut.isPending}
                                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-600 hover:bg-emerald-500 text-white shadow transition-colors"
                                                    >
                                                        <Check className="w-3.5 h-3.5" />
                                                        <span>Marker som gjort</span>
                                                    </button>
                                                </>
                                            )}

                                            {/* Udskyd (Snooze) Button & Quick Options */}
                                            {snoozeMenuId === p.id ? (
                                                <div className="flex items-center gap-1.5 bg-slate-900 border border-slate-700 rounded-lg px-2 py-1">
                                                    <span className="text-[11px] text-slate-400">Udskyd:</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSnooze(p, 3, '3 timer', false)}
                                                        className="px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-[11px] font-medium text-amber-300 border border-slate-700"
                                                    >
                                                        3 timer
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSnooze(p, 24, 'Til i morgen', true)}
                                                        className="px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-[11px] font-medium text-slate-200 border border-slate-700"
                                                    >
                                                        Til i morgen
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setSnoozeMenuId(null)}
                                                        className="p-0.5 text-slate-400 hover:text-white"
                                                        title="Annuller"
                                                    >
                                                        <X className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => setSnoozeMenuId(p.id)}
                                                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-slate-800/90 hover:bg-slate-700 text-slate-300 border border-slate-700 transition-colors"
                                                    title="Udskyd dette punkt (f.eks. mens vi afventer bank-svar eller mere data)"
                                                >
                                                    <Clock className="w-3.5 h-3.5 text-amber-400" />
                                                    <span>Udskyd</span>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}

                        {tab === 'history' && historyProposals.length === 0 && (
                            <div className="text-center py-6 text-xs text-slate-400">
                                Ingen tidligere beslutninger registreret endnu.
                            </div>
                        )}

                        {tab === 'history' &&
                            historyProposals.map((p) => {
                                const snoozed = snoozeMap[String(p.id)];
                                return (
                                    <div
                                        key={p.id}
                                        className="rounded-xl bg-slate-950/50 border border-slate-800/80 p-3 space-y-1"
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="text-xs font-semibold text-slate-200">{p.title}</span>
                                            <span
                                                className={`text-[10px] px-2 py-0.5 rounded-full font-semibold shrink-0 ${
                                                    p.status === 'applied'
                                                        ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
                                                        : snoozed
                                                        ? 'bg-amber-500/15 text-amber-300 border border-amber-500/30'
                                                        : 'bg-slate-800 text-slate-400 border border-slate-700'
                                                }`}
                                            >
                                                {p.status === 'applied'
                                                    ? '✓ Gjort / Godkendt'
                                                    : snoozed
                                                    ? `⏰ Udskudt (${snoozed.label})`
                                                    : 'Udskudt / Afvist'}
                                            </span>
                                        </div>
                                        <p className="text-[11px] text-slate-400 line-clamp-2">{p.rationale}</p>
                                        <div className="flex items-center justify-between pt-0.5">
                                            <div className="text-[10px] text-slate-500 flex items-center gap-1">
                                                <Clock className="w-3 h-3" />
                                                <span>
                                                    {snoozed
                                                        ? `Udskudt til ${new Date(snoozed.until).toLocaleTimeString('da-DK', {
                                                              hour: '2-digit',
                                                              minute: '2-digit',
                                                          })}`
                                                        : p.decided_at
                                                        ? new Date(p.decided_at).toLocaleString('da-DK', {
                                                              day: '2-digit',
                                                              month: 'short',
                                                              hour: '2-digit',
                                                              minute: '2-digit',
                                                          })
                                                        : ''}
                                                </span>
                                            </div>
                                            {snoozed && p.status === 'pending' && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleUnsnooze(p.id)}
                                                    className="text-[11px] font-medium text-amber-400 hover:text-amber-300"
                                                >
                                                    Tag op igen
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                    </div>

                    {/* Footer */}
                    <div className="p-3 bg-slate-950/90 border-t border-slate-800 flex items-center justify-between text-xs">
                        <span className="text-[11px] text-slate-400">
                            Dagsbudget (07:30–23:00):{' '}
                            <strong className="text-white">{fmtDkk(snap?.current_daytime_budget_dkk || 4000)}</strong>
                        </span>
                        <button
                            onClick={() => {
                                setOpen(false);
                                navigate('/simplyloan/meta');
                            }}
                            className="flex items-center gap-1 text-orange-400 hover:text-orange-300 font-semibold"
                        >
                            <span>Åbn Meta Dashboard</span>
                            <ExternalLink className="w-3.5 h-3.5" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
